/**
 * How a person and an organisation are DRAWN — the two letters in a face, and which of the
 * tenant's six tints sits behind them. Four modules each carried their own copy of `initials()`
 * before this file; three of them were the same function and all four were wrong about companies.
 *
 * 🔴 Nothing here is an input to anything. No sort, no filter, no score, no match reads a tint or
 * an initial — they are a picture a human looks at to find a row (D15, D25).
 */

/** A person is a circle, an organisation is a squircle. That is the only difference the kit draws. */
export type FaceKind = "person" | "organisation";

/**
 * What the Passport did with the photo — the SAME five words each module's `useFace` already
 * returns, so a call site hands its state straight over with nothing to translate. `refused` is not
 * an error and is not drawn as one: the grant simply does not open this picture to this session (D25).
 */
export type FaceState = "none" | "loading" | "ready" | "refused" | "failed";

/**
 * Legal forms, stripped for DISPLAY only. "Freshness First (Pty) Ltd" initialises to **FF**, not
 * FL — the suffix is a registration fact, not part of what anyone calls the company.
 *
 * ⚠️ This is not a jurisdiction list and must never become one (D4/D16). It never reaches the
 * schema, a rule pack, or a comparison; it decides two letters in a 32px circle. The canonical
 * legal form lives on the organisation as an ISO 20275 ELF code, which is where a rule reads it.
 */
const LEGAL_FORMS = new Set([
  "ab", "ag", "aps", "as", "bhd", "bv", "cc", "co", "company", "corp", "corporation", "gmbh",
  "inc", "kft", "limited", "llc", "llp", "lp", "ltd", "ltda", "nv", "oy", "plc", "pte", "pty",
  "sa", "sarl", "sas", "sdn", "spa", "srl", "trust", "ug", "zrt",
]);

const strip = (word: string): string => word.replace(/[.,()[\]]/g, "").toLowerCase();

/**
 * The words a reader would actually say. For an organisation that means dropping a trailing run
 * of legal forms — only trailing, so "Trust Films" keeps its Trust and "Films Trust" loses it.
 */
export function nameWords(name: string, kind: FaceKind = "person"): string[] {
  const words = name.split(/[\s\u00a0]+/).filter((word) => strip(word) !== "");
  if (kind !== "organisation") return words;
  let end = words.length;
  while (end > 1 && LEGAL_FORMS.has(strip(words[end - 1] ?? ""))) end -= 1;
  const kept = words.slice(0, end);
  return kept.length > 0 ? kept : words;
}

/**
 * Two letters, from the first and last word a reader would say. One word gives one letter, not two
 * from the same word — "Netflix" is N, and NE would read as a second company.
 */
export function initials(name: string | null, kind: FaceKind = "person"): string {
  if (name === null) return "?";
  const words = nameWords(name, kind);
  if (words.length === 0) return "?";
  const first = [...(words[0] ?? "")][0] ?? "";
  const last = words.length > 1 ? ([...(words[words.length - 1] ?? "")][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

/** How many tints `ui.css` declares (`.pk-face--t0` … `--t5`), all of them the tenant's own roles. */
export const FACE_TINTS = 6;

/**
 * A stable tint for a name: the same person is the same colour on every screen, in every session,
 * on every machine — so a reader learns a face before they read its label. FNV-1a over code points,
 * which is small, deterministic and has no seed to drift.
 */
export function tintIndex(name: string | null, kind: FaceKind = "person"): number {
  const key = name === null ? "" : nameWords(name, kind).join(" ").toLowerCase();
  let hash = 0x811c9dc5;
  for (const point of key) {
    hash ^= point.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % FACE_TINTS;
}

/** What the browser should say about a face that is not a photograph. An empty string means "the name". */
export function faceTitle(state: FaceState, name: string | null): string {
  switch (state) {
    case "refused":
      return "This photo was not opened to your session";
    case "failed":
      return "The photo could not be loaded";
    case "none":
      return "No photo on file";
    // `loading` says nothing on purpose: a tooltip that flickers "loading" and then a name reads as
    // a fault. The name is true throughout.
    default:
      return name ?? "";
  }
}
